import { useState, type ReactElement } from 'react';
import { Input } from '../../components/forms/Input';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';

export interface ClampedNumberInputProps {
	/** The stored value, or undefined when the server default applies. */
	value: number | undefined;
	/** Shown when nothing is stored, so the field states the effective setting. */
	fallback?: number;
	min: number;
	/** Omitted for a field the server does not cap, such as a byte size. */
	max?: number;
	/** False for a field that accepts decimals, such as a dollar budget. */
	integer?: boolean;
	step?: number;
	placeholder?: string;
	/** Called on blur and on Enter with the clamped value, never mid-typing. */
	onCommit: (value: number | undefined) => void;
	/** The bounds sentence rendered under the field. Already translated. */
	hint: string;
}

/**
 * A bounded number field that clamps when the person LEAVES it.
 *
 * Clamping on every keystroke makes a field untypable whenever its minimum has
 * more digits than the first thing typed: the first character of 100000 is 1,
 * which clamps straight back up to 100000, so the value can never be reached by
 * typing. Decimals are worse — the "0." of 0.4 parses as 0 and is clamped to the
 * minimum before the digits arrive.
 *
 * The typed text is therefore held here and committed on blur or Enter, which is
 * also when the value is clamped. While a value outside the bounds is being
 * typed the field says what leaving it will do, so the clamp is never a
 * surprise.
 *
 * Shared by the step-configuration forms: every one of them mirrors a set of
 * server-side clamps, and the bug is the same wherever the pattern is retyped.
 */
export function ClampedNumberInput({
	value,
	fallback,
	min,
	max,
	integer = true,
	step,
	placeholder,
	onCommit,
	hint,
}: ClampedNumberInputProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	// Null means "not being edited": the field then renders the stored value, so
	// a config replaced from outside the form is picked up without any syncing.
	const [draft, setDraft] = useState<string | null>(null);

	const storedText =
		value != null ? String(value) : fallback != null ? String(fallback) : '';

	const parseDraft = (text: string): number | null => {
		const parsed = integer ? parseInt(text, 10) : parseFloat(text);

		if (isNaN(parsed)) {
			return null;
		}

		// Decimal fields are money or a fraction, so two places is the whole
		// precision either one carries.
		return integer ? parsed : Math.round(parsed * 100) / 100;
	};

	const clamp = (parsed: number): number =>
		Math.max(min, max == null ? parsed : Math.min(max, parsed));

	const commit = (): void => {
		if (draft == null) {
			return;
		}

		const parsed = parseDraft(draft);

		setDraft(null);

		if (parsed == null) {
			// Clearing the field falls back to the server default rather than zero.
			onCommit(undefined);
			return;
		}

		onCommit(clamp(parsed));
	};

	const pending = draft == null ? null : parseDraft(draft);
	const outOfRange =
		pending != null && (pending < min || (max != null && pending > max));

	return (
		<>
			<Input
				type="number"
				value={draft ?? storedText}
				onChange={(e) => {
					setDraft(e.target.value);
				}}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						// The config form sits inside the flow builder; without this the
						// key would bubble up and submit it.
						e.preventDefault();
						commit();
					}
				}}
				min={min}
				max={max}
				step={step}
				placeholder={placeholder}
			/>
			{outOfRange ? (
				<p className={`text-xs mt-1 ${colors.classes.textWarning}`}>
					{t('Outside the allowed range. Leaving this field sets it to')}{' '}
					{t(String(clamp(pending)))}
					{t('.')}
				</p>
			) : null}
			<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>{hint}</p>
		</>
	);
}
